import { LitElement, css, html } from "lit";

const MP_SCRIPT_SRC = "https://sdk.mercadopago.com/js/v2";
const MP_SCRIPT_ATTR = "data-mercadopago-sdk";
let loadMercadoPagoLoader = null;

const FALLBACK_LANG = "es";

const LABELS = {
  es: {
    open: "Pagar con MercadoLibre",
    loading: "Cargando checkout…"
  },
  en: {
    open: "Pay with MercadoLibre",
    loading: "Loading checkout…"
  }
};

const resolveLang = (value) => {
  if (typeof value === "string" && value.trim()) {
    const normalized = value.trim().toLowerCase();
    if (normalized.startsWith("es")) return "es";
    if (normalized.startsWith("en")) return "en";
  }
  if (typeof navigator !== "undefined") {
    const locale = navigator.language || navigator.languages?.[0] || "";
    if (locale.toLowerCase().startsWith("es")) return "es";
  }
  return "en";
};

const sumItems = (items = []) =>
  items.reduce((total, item) => {
    const price = Number(item?.unit_price ?? item?.price ?? 0);
    const qty = Number(item?.quantity ?? 1);
    if (!Number.isFinite(price) || !Number.isFinite(qty)) return total;
    return total + price * qty;
  }, 0);

const normalizeItems = (items = []) =>
  items
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const quantity = Number(item.quantity ?? 1) || 1;
      const unitPrice = Number(item.unit_price ?? item.price ?? 0) || 0;
      return {
        id: item.id || `item-${index + 1}`,
        title: item.title || item.description || "Item",
        description: item.description || item.title || "",
        category_id: item.category_id || "credits",
        quantity,
        unit_price: unitPrice,
        currency_id: item.currency_id || "USD",
      };
    })
    .filter(Boolean);

const clone = (value) => {
  if (value === null || value === undefined) return value ?? null;
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(value);
    } catch {
      // fallback to JSON serialization
    }
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

class NearMercadoPago extends LitElement {
  static properties = {
    lang: { type: String },
    publicKey: { type: String, attribute: "public-key" },
    apiBase: { type: String, attribute: "api-base" },
    items: { attribute: false },
    metadata: { attribute: false },
    disabled: { type: Boolean, reflect: true },
    busy: { state: true },
    active: { state: true }
  };


  /*createRenderRoot() {
    return this;
  }*/

  static styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .button-wrapper {
      display: flex;
      justify-content: center;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      padding: 0.6rem 1.1rem;
      border-radius: 999px;
      border: none;
      cursor: pointer;
      font: inherit;
      font-weight: 600;
      background: var(--nc-accent, #4f46e5);
      color: var(--nc-button-text, #ffffff);
      transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
      box-shadow: 0 10px 24px color-mix(in srgb, var(--nc-accent, #4f46e5) 25%, transparent);
    }

    button:hover,
    button:focus-visible {
      transform: translateY(-1px);
      background: color-mix(in srgb, var(--nc-accent, #4f46e5) 92%, #ffffff 8%);
      box-shadow: 0 14px 30px color-mix(in srgb, var(--nc-accent, #4f46e5) 35%, transparent);
    }

    button:active {
      transform: translateY(0);
    }

    button[disabled] {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: 0 6px 16px color-mix(in srgb, var(--nc-accent, #4f46e5) 20%, transparent);
    }

    .brick-wrapper {
      width: 100%;
      min-height: 420px;
      border-radius: 18px;
      border: 1px solid rgba(148, 163, 184, 0.15);
      padding: 1rem;
      background: color-mix(in srgb, var(--nc-card-background, #0f172a) 96%, transparent);
      box-shadow: 0 24px 48px rgba(15, 23, 42, 0.15);
    }

    .brick-wrapper.none {
      display: none;
    }

    .payment_container {
      width: 100%;
    }
  `;

  constructor() {
    super();
    this.lang = "";
    this.publicKey = "";
    this.apiBase = "/api";
    this.items = [];
    this.metadata = null;
    this.disabled = false;
    this.busy = false;
    this.active = false;
    this._sdkPromise = null;
    this._mp = null;
    this._mpKey = "";
    this._mpLocale = "";
    this._brickController = null;
    this._containerId = `checkout-container`;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.lang) {
      this.lang = resolveLang(this.getAttribute("lang"));
    }
    if (this.publicKey) {
      this.#ensureSdk().catch((error) => {
        console.warn("[near-mercado-pago] SDK preload failed", error);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyBrick();
  }

  updated(changedProps) {
    if (changedProps.has("lang")) {
      this.lang = resolveLang(this.lang);
    }
    if (changedProps.has("publicKey") && this.publicKey) {
      this.#ensureSdk().catch((error) => {
        console.warn("[near-mercado-pago] SDK load failed", error);
      });
    }
    if (changedProps.has("items") && this.active) {
      const previous = sumItems(changedProps.get("items") || []);
      const current = sumItems(this.items || []);
      if (Math.abs(previous - current) > 0.005) {
        this.#remountBrick().catch((error) => {
          console.warn("[near-mercado-pago] unable to refresh payment brick", error);
          this.#emit("payment-error", { error });
        });
      }
    }
  }

  render() {
    const lang = this.#resolvedLang;
    const strings = LABELS[lang] || LABELS.en;
    const amount = sumItems(this.items);
    const disabled =
      this.disabled || this.busy || !this.publicKey || !Number.isFinite(amount) || amount <= 0;
    const label = this.busy ? strings.loading : strings.open;
    
    return html`
      ${this.active
        ? html`
            <div class="brick-wrapper" aria-live="polite">
              <slot></slot>
            </div>
          `
        : html`
            <div class="button-wrapper">
              <button type="button" @click=${() => this.#openBrick()} ?disabled=${disabled}>
                ${label}
              </button>
            </div>
          `}
    `;
  }

  get #resolvedLang() {
    return resolveLang(this.lang || FALLBACK_LANG);
  }

  get #apiBaseUrl() {
    const base = typeof this.apiBase === "string" ? this.apiBase.trim() : "";
    if (!base) return "/api";
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }

  async #ensureSdk() {
    if (typeof window === "undefined" || !this.publicKey) return null;

    const locale = "es-ar"; //this.#resolvedLang === "es" ? "es-AR" : "en-US";
    if (this._mp && this._mpKey === this.publicKey && this._mpLocale === locale) {
      return this._mp;
    }
    try {
      const SDK = await this.#loadMercadoPagoCtor();
      if (!SDK) {
        throw new Error("MercadoPago SDK unavailable");
      }
      const instance = new SDK(this.publicKey, { locale });
      this._mp = instance;
      this._mpKey = this.publicKey;
      this._mpLocale = locale;
      return this._mp;
    } catch (error) {
      console.warn("[near-mercado-pago] failed creating MercadoPago instance", error);
      this._mp = null;
      return null;
    }
  }

  async #loadMercadoPagoCtor() {
    if (typeof window === "undefined") return null;
    if (typeof window.MercadoPago === "function") {
      return window.MercadoPago;
    }

    if (this._sdkPromise) {
      await this._sdkPromise;
      return typeof window.MercadoPago === "function" ? window.MercadoPago : null;
    }

    this._sdkPromise = (async () => {
      if (!loadMercadoPagoLoader) {
        try {
          const module = await import("@mercadopago/sdk-js");
          loadMercadoPagoLoader =
            module.loadMercadoPago || module.default?.loadMercadoPago || null;
        } catch (error) {
          console.warn("[near-mercado-pago] unable to import @mercadopago/sdk-js", error);
        }
      }

      if (typeof loadMercadoPagoLoader === "function") {
        try {
          await loadMercadoPagoLoader();
          return;
        } catch (error) {
          console.warn("[near-mercado-pago] npm loader failed, falling back to script tag", error);
        }
      }

      const existing = document.querySelector(`script[${MP_SCRIPT_ATTR}]`);
      const script = existing || document.createElement("script");
      if (!existing) {
        script.src = MP_SCRIPT_SRC;
        script.async = true;
        script.setAttribute(MP_SCRIPT_ATTR, "true");
        document.head.appendChild(script);
      }

      await new Promise((resolve, reject) => {
        script.addEventListener("load", resolve, { once: true });
        script.addEventListener("error", () => reject(new Error("MercadoPago SDK failed to load")), {
          once: true
        });
      });
    })();

    try {
      await this._sdkPromise;
      return typeof window.MercadoPago === "function" ? window.MercadoPago : null;
    } finally {
      this._sdkPromise = null;
    }
  }

  async #openBrick() {
    if (this.disabled || this.busy) return;

    const amount = sumItems(this.items);
    if (!Number.isFinite(amount) || amount <= 0) {
      this.#emit("payment-error", { error: new Error("Invalid amount") });
      return;
    }

    const proceed = this.#emit(
      "payment-start",
      {
        amount,
        items: clone(this.items),
        metadata: clone(this.metadata)
      },
      { cancelable: true }
    );
    if (!proceed) {
      return;
    }

    this.busy = true;

    try {
      const mp = await this.#ensureSdk();
      if (!mp) {
        throw new Error("MercadoPago SDK unavailable");
      }
      this.active = true;
      await this.updateComplete;
      await this.#createBrick({ mp, amount });
    } catch (error) {
      this.busy = false;
      this.active = false;
      this.#emit("payment-error", { error });
    }
  }

  async #remountBrick() {
    if (!this.active) return;
    await this.updateComplete;
    const mp = await this.#ensureSdk();
    if (!mp) {
      throw new Error("MercadoPago SDK unavailable");
    }
    const amount = sumItems(this.items);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Invalid amount");
    }
    await this.#createBrick({ mp, amount });
  }

  

  async #createBrick({ mp, amount }) {
    this.#destroyBrick();
    const container = document.querySelector(`#${this._containerId}`);
    if (!container) {
      throw new Error("Payment container missing");
    }
    // create preference on the server
    const preferenceBody={
      items: normalizeItems(this.items),
      //metadata: this.metadata || {},
      external_reference: `${this.metadata?.tokenId || "unknown"}-${Date.now()}`,
      redirectMode: 'self' 
    }
    const response = await fetch(`${this.#apiBaseUrl}/create-preference`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(preferenceBody)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data || !data.id) {
      const error = new Error("create-preference failed");
      error.response = data;
      error.status = response.status;
      throw error;
    }
    const preferenceId = data.id;
    const builder = mp.bricks();
    this.busy = true;

    this._brickController = await builder.create("payment", this._containerId, {
      initialization: {
        amount: Number(amount),
        preferenceId: preferenceId,
        redirectMode: 'self'
      },
      customization: {
        paymentMethods: {
          //creditCard: "all",
          //debitCard: "all",
          //ticket: "all",
          //bankTransfer: "all",
          //onboarding_credits: "all",
          mercadoPago: "all",
          maxInstallments: 1
        }
      },
      callbacks: {
        onReady: () => {
          this.busy = false;
          this.#emit("payment-ready");
        },
        
        onSubmit: ({ selectedPaymentMethod, formData }) => {
          // callback llamado al hacer clic en el botón de envío de datos
          console.log("Submitting payment with method:", selectedPaymentMethod);
          formData = this.#addMetadataToPayload(formData);
          try {
              formData.additional_info = formData.additional_info || {};
              formData.additional_info.items = normalizeItems(this.items).map(item => {
                delete item.currency_id;
                return item;
              }) || [];

              console.log("Normalized items for additional_info:", formData.additional_info);
          } catch (error) {
              console.error("Error normalizing items:", error);

          }

          return new Promise((resolve, reject) => {
              fetch(`${this.#apiBaseUrl}/process-payment`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
              })
              .then((response) =>{ 
                if (!response.ok) throw new Error("Network response was not ok"); 
                return response.json(); })
              .then((response) => {
                // recibir el resultado del pago
                console.log("Payment response:", response);
                this.busy = false;
                this.active = false;
                this.#destroyBrick();
                this.#emit("payment-success", {
                  result:response,
                  amount:response.transaction_amount,
                  items: clone(this.items),
                  metadata: clone(this.metadata)
                });
                resolve();
              })
              .catch((error) => {
                this.busy = false;
                this.active = false;
                this.#destroyBrick();
                // manejar la respuesta de error al intentar crear el pago
                console.error("Payment error:", error);
                this.#emit("payment-error", { error });
                reject();
              });
          });
        },
        onError: (error) => {
          this.busy = false;
          console.error("Payment error:", error);
          this.#emit("payment-error", { error });
        }
      }
    });
  }

  #addMetadataToPayload(payload) {
    if (typeof this.metadata === "object" && this.metadata) {
      payload.nearMetadata = this.metadata;
      payload.external_reference = `${this.metadata?.tokenId || "unknown"}-${Date.now()}`;
    }
    return payload;
  }

  #decoratePaymentPayload(formData, amount) {
    const payload = clone(formData || {});
    payload.transaction_amount = Number(amount);
    payload.description = payload.description || this.#firstItemTitle();
    payload.metadata = {
      ...(payload.metadata || {}),
      ...(typeof this.metadata === "object" && this.metadata ? this.metadata : {})
    };
    const items = normalizeItems(this.items);
    if (items.length) {
      payload.additional_info = {
        ...(payload.additional_info || {}),
        items
      };
    }
    if (!payload.external_reference) {
      payload.external_reference = `mp-payment-${Date.now()}`;
    }
    return payload;
  }

  #firstItemTitle() {
    return this.items?.[0]?.title || this.items?.[0]?.description || "Credits purchase";
  }

  #destroyBrick() {
    if (this._brickController) {
      if (typeof this._brickController.destroy === "function") {
        this._brickController.destroy();
      } else if (typeof this._brickController.unmount === "function") {
        this._brickController.unmount();
      }
    }
    this._brickController = null;
  }

  #emit(name, detail = {}, options = {}) {
    const event = new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
      cancelable: Boolean(options.cancelable)
    });
    const dispatched = this.dispatchEvent(event);
    if (options.cancelable && !dispatched) {
      return false;
    }
    return true;
  }
}

if (!customElements.get("near-mercado-pago")) {
  customElements.define("near-mercado-pago", NearMercadoPago);
}

export { NearMercadoPago };
